import { useMemo } from 'react';
import { createStaticDataProvider } from '@adv-frontend/data-provider';
import { createFilterSelectWithSuggest } from '@adv-frontend/filters';
import { type OpenApiSchemaPriceIndexSignal } from '#shared/api/schema';
import { trimAndLowerString } from '#shared/lib/string';
import { PRICE_INDEX_COUNTS_FILTER_ID, PRICE_INDEX_COUNTS_FILTER_TITLE } from '../../constants';

type VisiblePriceIndexSignal = Exclude<OpenApiSchemaPriceIndexSignal, 'bad_data'>;

type Item<ID extends VisiblePriceIndexSignal = VisiblePriceIndexSignal> = {
    id: ID;
    label: string;
};

const items: { [key in VisiblePriceIndexSignal]: Item<key> } = {
    high: {
        id: 'high',
        label: 'Выше другой площадки',
    },
    medium: {
        id: 'medium',
        label: 'На уровне другой площадки',
    },
    low: {
        id: 'low',
        label: 'Выгодная цена',
    },
    no_data: {
        id: 'no_data',
        label: 'Отсутствует',
    },
} as const;

export const isPriceIndexSignal = (value: string): value is VisiblePriceIndexSignal =>
    Object.keys(items).includes(value);

export const createPriceIndexSignalsFilter = () =>
    createFilterSelectWithSuggest<string, Item>({
        id: PRICE_INDEX_COUNTS_FILTER_ID,
        title: PRICE_INDEX_COUNTS_FILTER_TITLE,
        suggestProps: {
            dataProvider: function Component(props) {
                const searchString = trimAndLowerString(props.queryArg.searchValue);

                const filteredItems = useMemo(
                    () => Object.values(items).filter((item) => trimAndLowerString(item.label).includes(searchString)),
                    [searchString]
                );

                const dataProvider = useMemo(() => createStaticDataProvider(filteredItems), [filteredItems]);

                return props.children(dataProvider);
            },
            renderSelectListItem: ({ item }) => item.label,
            renderFilterChipsItem: ({ item }) => item.label,
            useTitleByValue: (values: string[]) =>
                values.map((value) => {
                    return { value: value, title: isPriceIndexSignal(value) ? items[value].label : value };
                }),
        },
    });
